import { PenLine, MessageSquare } from 'lucide-react';
import type { Station } from '@hcc/types';
import { stationLabel } from '@hcc/types';
import type { PersonApi } from '../../hooks/usePeople';
import { FlagsButton } from '../../components/person/FlagsButton';
import { PersonAvatar } from '../../components/person/PersonAvatar';
import { Button } from '../../components/ui/Button';
import { Pill } from '../../components/ui/Pill';

interface ToIssueSectionProps {
  station: Station;
  people: PersonApi[];
  searching: boolean;
  /** How many on-site people are hidden by the "allocated only" filter. */
  unallocatedHidden: number;
  showUnallocated: boolean;
  onToggleUnallocated: () => void;
  onIssue: (person: PersonApi) => void;
  onNotes: (person: PersonApi) => void;
}

/** The primary work list: people still to be issued kit at this station. */
export function ToIssueSection({
  station,
  people,
  searching,
  unallocatedHidden,
  showUnallocated,
  onToggleUnallocated,
  onIssue,
  onNotes,
}: ToIssueSectionProps) {
  return (
    <section
      aria-label="To issue"
      className="mt-4 overflow-hidden rounded-token-lg border border-line/10 bg-surface shadow-token-sm"
    >
      <header className="flex items-center justify-between border-b border-divider/10 px-5 py-3">
        <div className="flex items-center gap-2">
          <PenLine className="h-3.5 w-3.5 text-accent" />
          <span className="text-xs font-semibold uppercase tracking-wider text-fg-muted">
            To issue
          </span>
          <Pill tone="accent" size="sm">
            {people.length}
          </Pill>
        </div>
        {!searching && (unallocatedHidden > 0 || showUnallocated) ? (
          <button
            type="button"
            onClick={onToggleUnallocated}
            aria-pressed={showUnallocated}
            className="text-[11px] font-medium text-fg-muted underline-offset-2 transition-colors hover:text-fg hover:underline"
          >
            {showUnallocated ? 'Allocated only' : `Show ${unallocatedHidden} without kit`}
          </button>
        ) : null}
      </header>
      {people.length === 0 ? (
        <div className="px-4 py-6 text-center text-sm text-fg-muted">
          {searching
            ? 'No matches still to issue.'
            : unallocatedHidden > 0
              ? `All allocated ${stationLabel(station)} kit issued. ${unallocatedHidden} other ${
                  unallocatedHidden === 1 ? 'person' : 'people'
                } on site ${
                  unallocatedHidden === 1 ? 'has' : 'have'
                } none allocated here — use “Show ${unallocatedHidden} without kit” to issue anyway.`
              : `All ${stationLabel(station)} kit issued. Nice work.`}
        </div>
      ) : (
        <ul className="divide-y divide-divider/10">
          {people.map((p) => (
            <li
              key={p.id}
              className="flex flex-col gap-3 px-5 py-4 sm:flex-row sm:items-center sm:justify-between sm:gap-4"
            >
              <div className="flex min-w-0 flex-1 items-center gap-3">
                <PersonAvatar fullName={p.full_name} photoKey={p.current_photo_s3_key} size="md" />
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-base font-semibold text-fg">{p.full_name}</span>
                    <FlagsButton
                      personId={p.id}
                      personName={p.full_name}
                      count={p.active_flags ?? 0}
                    />
                  </div>
                  <div className="mt-0.5 text-xs text-fg-muted">
                    {p.position ?? 'Position unknown'}
                    {p.email ? ` · ${p.email}` : ''}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="ghost"
                  size="sm"
                  iconLeft={<MessageSquare className="h-3.5 w-3.5" />}
                  onClick={() => onNotes(p)}
                >
                  Notes
                </Button>
                <Button
                  variant="primary"
                  size="md"
                  iconLeft={<PenLine className="h-4 w-4" />}
                  onClick={() => onIssue(p)}
                >
                  Issue kit
                </Button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
